import { useMemo, useState } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'
import {
  CATEGORY_LABELS,
  RecurrenceFrequency,
  ScheduleTeam,
  ScheduleUser,
  ShiftCategory,
  ShiftTemplate,
  WEEKDAYS_PL,
  formatDateISO,
} from './utils'

interface Props {
  token: string
  clientId: number
  templates: ShiftTemplate[]
  users: ScheduleUser[]
  teams?: ScheduleTeam[]
  onClose: () => void
  onDone: () => void
}

export default function BulkAssignModal({
  token,
  clientId,
  templates,
  users,
  teams = [],
  onClose,
  onDone,
}: Props) {
  const { showError, showSuccess } = useNotificationContext()
  const [busy, setBusy] = useState(false)

  const today = formatDateISO(new Date())
  const [dateFrom, setDateFrom] = useState<string>(today)
  const [dateTo, setDateTo] = useState<string>(today)
  const [selectedUsers, setSelectedUsers] = useState<number[]>([])
  const [selectedTeams, setSelectedTeams] = useState<number[]>([])
  const [userFilter, setUserFilter] = useState('')
  const [weekdays, setWeekdays] = useState<number[]>([0, 1, 2, 3, 4]) // pn-pt domyślnie
  const [templateId, setTemplateId] = useState<number | ''>('')
  const [useCustom, setUseCustom] = useState<boolean>(false)
  const [customLabel, setCustomLabel] = useState('')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [customColor, setCustomColor] = useState('#3b82f6')
  const [customCategory, setCustomCategory] = useState<ShiftCategory>('shift')
  const [note, setNote] = useState('')
  const [skipExisting, setSkipExisting] = useState(true)

  // Powtarzanie
  const [frequency, setFrequency] = useState<RecurrenceFrequency>('')
  const [interval, setIntervalVal] = useState<number>(1)

  const toggleUser = (id: number) => {
    setSelectedUsers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }
  const toggleTeam = (id: number) => {
    setSelectedTeams(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }
  const toggleWeekday = (i: number) => {
    setWeekdays(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i].sort((a, b) => a - b))
  }
  const selectAllUsers = () => setSelectedUsers(users.map(u => u.id))
  const selectNoneUsers = () => setSelectedUsers([])

  const filteredUsers = useMemo(() => {
    if (!userFilter.trim()) return users
    const q = userFilter.toLowerCase()
    return users.filter(u =>
      (u.full_name || '').toLowerCase().includes(q) || (u.username || '').toLowerCase().includes(q)
    )
  }, [users, userFilter])

  // Podgląd: ile osób finalnie zostanie (suma user_ids + członkowie zespołów, unikalnie)
  const effectiveUserCount = useMemo(() => {
    const ids = new Set<number>(selectedUsers)
    for (const tid of selectedTeams) {
      const t = teams.find(x => x.id === tid)
      if (!t) continue
      for (const m of t.members) if ((m as any).is_active !== false) ids.add(m.id)

    }
    return ids.size
  }, [selectedUsers, selectedTeams, teams])

  const handleSubmit = async () => {
    if (effectiveUserCount === 0) {
      showError('Wybierz przynajmniej jedną osobę lub zespół')
      return
    }
    if (!dateFrom || !dateTo) { showError('Wybierz zakres dat'); return }
    if (!useCustom && !templateId) { showError('Wybierz szablon lub włącz tryb "własne godziny"'); return }
    if (!frequency && weekdays.length === 0) { showError('Zaznacz przynajmniej jeden dzień tygodnia'); return }
    if (frequency === 'weekly' && weekdays.length === 0) { showError('Dla powtarzania tygodniowego wskaż dni'); return }

    setBusy(true)
    try {
      const body: any = {
        client_id: clientId,
        user_ids: selectedUsers,
        team_ids: selectedTeams,
        date_from: dateFrom,
        date_to: dateTo,
        note: note || null,
        skip_existing: skipExisting,
      }
      // Częstotliwość
      if (frequency) {
        body.frequency = frequency
        body.interval = Math.max(1, Number(interval) || 1)
      }
      // Weekdays: dla "daily"/"monthly" przekazujemy null (chyba że użytkownik świadomie nie odznaczył)
      if (frequency === 'daily') {
        // ignorujemy weekdays
      } else {
        body.weekdays = weekdays
      }

      if (!useCustom) {
        body.shift_template_id = Number(templateId)
      } else {
        body.shift_template_id = templateId ? Number(templateId) : null
        body.custom_label = customLabel || null
        body.custom_start_time = customStart ? `${customStart}:00` : null
        body.custom_end_time = customEnd ? `${customEnd}:00` : null
        body.custom_color = customColor || null
        body.custom_category = customCategory || null
      }

      const r = await fetch('/api/schedule/assignments/bulk', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!r.ok) throw new Error(await r.text())
      const out = await r.json()
      showSuccess(`Utworzono ${out.created} wpisów (pominięto ${out.skipped}).`)
      onDone()
    } catch (e: any) {
      showError(`Błąd: ${e?.message || e}`)
    } finally {
      setBusy(false)
    }
  }

  const frequencyHint = useMemo(() => {
    const n = Math.max(1, Number(interval) || 1)
    if (frequency === 'daily') {
      return n === 1 ? 'Codziennie' : `Co ${n} dni`
    }
    if (frequency === 'weekly') {
      const dni = weekdays.length
        ? weekdays.map(w => WEEKDAYS_PL[w]).join(', ')
        : '(wybierz dni)'
      return n === 1 ? `Co tydzień (${dni})` : `Co ${n} tygodni (${dni})`
    }
    if (frequency === 'monthly') {
      return n === 1 ? 'Co miesiąc (ten sam dzień miesiąca co data startowa)' : `Co ${n} miesięcy (ten sam dzień)`
    }
    return 'Wszystkie dni w zakresie ograniczone wybranymi dniami tygodnia.'
  }, [frequency, interval, weekdays])

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[92vh] overflow-auto">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">📋 Masowe przypisanie</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Od">
              <input
                type="date"
                className="w-full border-gray-300 rounded-md shadow-sm"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
              />
            </Field>
            <Field label="Do">
              <input
                type="date"
                className="w-full border-gray-300 rounded-md shadow-sm"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
              />
            </Field>
          </div>

          {/* Powtarzanie */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 bg-indigo-50/40 rounded border border-indigo-100">
            <Field label="Powtarzanie">
              <select
                className="w-full border-gray-300 rounded-md shadow-sm"
                value={frequency}
                onChange={e => setFrequency(e.target.value as RecurrenceFrequency)}
              >
                <option value="">Bez (każdy dzień w zakresie)</option>
                <option value="daily">Codziennie / co N dni</option>
                <option value="weekly">Tygodniowo (co N tygodni)</option>
                <option value="monthly">Miesięcznie (co N miesięcy)</option>
              </select>
            </Field>
            <Field label="Co N">
              <input
                type="number"
                min={1}
                max={52}
                className="w-full border-gray-300 rounded-md shadow-sm disabled:bg-gray-100"
                value={interval}
                onChange={e => setIntervalVal(Number(e.target.value || 1))}
                disabled={!frequency}
              />
            </Field>
            <div className="sm:col-span-3 text-xs text-gray-600">
              <span className="font-medium">Podgląd zasady:</span> {frequencyHint}
            </div>
          </div>

          {/* Dni tygodnia – nieaktywne gdy daily */}
          <Field label={`Dni tygodnia${frequency === 'daily' ? ' (ignorowane przy codziennym)' : ''}`}>
            <div className="flex flex-wrap gap-1">
              {WEEKDAYS_PL.map((w, i) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => toggleWeekday(i)}
                  disabled={frequency === 'daily'}
                  className={`px-3 py-1 text-sm rounded border ${
                    weekdays.includes(i)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  } ${frequency === 'daily' ? 'opacity-50 cursor-not-allowed' : ''}`}
                >{w}</button>
              ))}
            </div>
          </Field>

          {/* Zespoły */}
          {teams.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">
                  Zespoły ({selectedTeams.length}/{teams.length})
                </span>
                <span className="text-xs text-gray-500">Wybór zespołu automatycznie doda jego członków.</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {teams.map(t => {
                  const active = selectedTeams.includes(t.id)
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => toggleTeam(t.id)}
                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors ${
                        active
                          ? 'text-white border-transparent'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                      style={active ? { backgroundColor: t.color || '#3b82f6' } : {}}
                      title={t.description || ''}
                    >
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: active ? '#ffffff' : (t.color || '#94a3b8') }}
                      />
                      {t.name}
                      <span className="ml-1 opacity-80">({t.member_count})</span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Indywidualne osoby */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-700">
                Osoby indywidualnie ({selectedUsers.length}/{users.length})
              </span>
              <div className="text-xs">
                <button onClick={selectAllUsers} className="text-blue-600 hover:underline">zaznacz wszystkich</button>
                <span className="mx-1 text-gray-400">·</span>
                <button onClick={selectNoneUsers} className="text-blue-600 hover:underline">odznacz</button>
              </div>
            </div>
            <input
              type="text"
              className="w-full border-gray-300 rounded-md shadow-sm mb-2 text-sm"
              placeholder="Filtruj po imieniu / loginie…"
              value={userFilter}
              onChange={e => setUserFilter(e.target.value)}
            />
            <div className="border border-gray-200 rounded max-h-44 overflow-auto">
              {filteredUsers.length === 0 && <div className="p-3 text-sm text-gray-500 italic">Brak osób</div>}
              {filteredUsers.map(u => (
                <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(u.id)}
                    onChange={() => toggleUser(u.id)}
                  />
                  <span className="flex-1">{u.full_name || u.username}</span>
                  {u.role && <span className="text-xs text-gray-400">{u.role}</span>}
                </label>
              ))}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Łącznie do przypisania: <strong>{effectiveUserCount}</strong>{' '}
              {effectiveUserCount === 1 ? 'osoba' : 'osób'} (z zespołów + indywidualnie, bez duplikatów)
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="inline-flex items-center text-sm">
              <input
                type="radio"
                className="mr-2"
                checked={!useCustom}
                onChange={() => setUseCustom(false)}
                disabled={!templates.length}
              />
              Użyj szablonu
            </label>
            <label className="inline-flex items-center text-sm">
              <input
                type="radio"
                className="mr-2"
                checked={useCustom}
                onChange={() => setUseCustom(true)}
              />
              Własne godziny / etykieta
            </label>
          </div>

          {!useCustom && (
            <Field label="Szablon zmiany">
              <select
                className="w-full border-gray-300 rounded-md shadow-sm"
                value={templateId}
                onChange={e => setTemplateId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">— wybierz —</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({(t.start_time || '').slice(0, 5)}–{(t.end_time || '').slice(0, 5)}) – {CATEGORY_LABELS[t.category]}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {useCustom && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-gray-50 rounded border border-gray-200">
              <Field label="Etykieta">
                <input
                  type="text"
                  className="w-full border-gray-300 rounded-md shadow-sm"
                  value={customLabel}
                  onChange={e => setCustomLabel(e.target.value)}
                />
              </Field>
              <Field label="Kategoria">
                <select
                  className="w-full border-gray-300 rounded-md shadow-sm"
                  value={customCategory}
                  onChange={e => setCustomCategory(e.target.value as ShiftCategory)}
                >
                  {(Object.entries(CATEGORY_LABELS) as [ShiftCategory, string][]).map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Start">
                <input type="time" className="w-full border-gray-300 rounded-md shadow-sm"
                  value={customStart} onChange={e => setCustomStart(e.target.value)} />
              </Field>
              <Field label="Koniec">
                <input type="time" className="w-full border-gray-300 rounded-md shadow-sm"
                  value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
              </Field>
              <Field label="Kolor" className="sm:col-span-2">
                <input type="color" className="h-9 w-16 border border-gray-300 rounded"
                  value={customColor} onChange={e => setCustomColor(e.target.value)} />
              </Field>
            </div>
          )}

          <Field label="Notatka (opcjonalna, dotyczy wszystkich wpisów)">
            <textarea
              className="w-full border-gray-300 rounded-md shadow-sm"
              rows={2}
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </Field>

          <label className="inline-flex items-center text-sm">
            <input
              type="checkbox"
              className="mr-2"
              checked={skipExisting}
              onChange={e => setSkipExisting(e.target.checked)}
            />
            Pomiń wpisy, które już istnieją (zalecane)
          </label>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
            onClick={onClose}
            disabled={busy}
          >
            Anuluj
          </button>
          <button
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            onClick={handleSubmit}
            disabled={busy}
          >
            {busy ? 'Zapisywanie…' : 'Utwórz wpisy'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children, className = '' }: { label: string, children: React.ReactNode, className?: string }) {
  return (
    <label className={`block text-sm ${className}`}>
      <span className="text-gray-700 font-medium">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  )
}
